import React from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { HealthStreamLogo } from "components/Images";
import PrinterIcon from "components/Images/PrinterIcon";
import PriceFormat from "components/PriceFormat";
import { countries } from "helpers";

const Receipt = ({ order }) => (
  <div className="receipt">
    <div className="receipt__header">
      <HealthStreamLogo className="receipt__logo" alt="Logo: HealthStream" />
      <h1>Receipt</h1>
    </div>
    <div className="receipt__row">
      <div className="receipt__info">
        <h3>Bill To</h3>
        <p id="name">{`${order.displayName}`}</p>
        <p id="email">{order.email}</p>
        <p id="address">{order.address1}</p>
        {order.address2 && <p>{order.address2}</p>}
        <p id="cityStateZip">{`${order.cityName}, ${order.stateName} ${order.postalCode}`}</p>
        <p id="countryName">{countries[order.countryName]}</p>
      </div>
      <div className="receipt__info">
        <h3>Order Number</h3>
        <p id="orderNumber">{order.orderNumber}</p>
      </div>
      <div className="receipt__info">
        <h3>Date</h3>
        <p id="orderDate">{moment(order.orderDate).format("M/D/YYYY")}</p>
      </div>
    </div>
    {order.cardType && (
      <div className="receipt__row">
        <div className="receipt__info">
          <h3>Paid Via</h3>
          <p id="cardType">Card Type: {order.cardType}</p>
          <p id="cardNumber">
            Card Number (last 4 digits): {order.creditCardNumber}
          </p>
        </div>
      </div>
    )}
    <div className="receipt__table">
      <table className="table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Item Price</th>
            <th>Discount</th>
            <th>Item Total</th>
          </tr>
        </thead>
        <tbody>
          {order.orderLineItems.map(receiptProduct => (
            <tr className="receipt__table-product" key={receiptProduct.sku}>
              <td>{receiptProduct.productName}</td>
              <td>
                <PriceFormat value={receiptProduct.retailPrice} />
              </td>
              <td>
                {Math.sign(receiptProduct.discount) === 0 ? (
                  <span>&mdash;</span>
                ) : (
                  <PriceFormat value={receiptProduct.discount} />
                )}
              </td>
              <td>
                <PriceFormat value={receiptProduct.unitPrice} />
              </td>
            </tr>
          ))}
          <tr className="receipt__table-summary">
            <td />
            <td />
            <td>
              <span>Subtotal:</span>
            </td>
            <td id="subtotal">
              <PriceFormat value={order.subTotal} />
            </td>
          </tr>
          {Math.sign(order.promotionalDiscount) !== 0 && (
            <tr className="receipt__table-summary">
              <td />
              <td />
              <td>
                <span>Promotion(s):</span>
              </td>
              <td id="promotionalDiscount">
                -<PriceFormat value={order.promotionalDiscount} />
              </td>
            </tr>
          )}
          <tr className="receipt__table-summary">
            <td />
            <td />
            <td>
              <span>Taxes:</span>
            </td>
            <td id="taxes">
              <PriceFormat value={order.taxCost} />
            </td>
          </tr>
          <tr className="receipt__table-total">
            <td />
            <td />
            <td>
              <span>Total:</span>
            </td>
            <td id="total">
              <PriceFormat value={order.total} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="receipt__footer">
      <div className="receipt__support">
        <span>
          If you have any questions regarding your order, please contact
          customer support at +1 800-521-0574 or visit{" "}
        </span>
        <a
          href={`${window.location.origin}/contact-us`}
          className="ms-1 d-inline-block"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://store.healthstream.com/contact-us
        </a>
        <span>. Keep this invoice for your records.</span>
      </div>
      <button
        className="receipt__print"
        type="button"
        aria-label="Print"
        onClick={() => window.print()}
      >
        <PrinterIcon alt="Print Icon" />
      </button>
    </div>
  </div>
);

Receipt.propTypes = {
  order: PropTypes.shape({
    omsOrderId: PropTypes.number,
    userId: PropTypes.number,
    orderNumber: PropTypes.string,
    orderDate: PropTypes.string,
    billingAddress: PropTypes.object,
    cardType: PropTypes.string,
    creditCardNumber: PropTypes.number,
    addressId: PropTypes.number,
    displayName: PropTypes.string,
    email: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    cityName: PropTypes.string,
    stateName: PropTypes.string,
    postalCode: PropTypes.string,
    countryName: PropTypes.string,
    orderLineItems: PropTypes.array,
    unitPrice: PropTypes.number,
    discount: PropTypes.number,
    promotionalDiscount: PropTypes.number,
    subTotal: PropTypes.number,
    taxCost: PropTypes.number,
    total: PropTypes.number
  }).isRequired
};

export default Receipt;
